<template>
  <div class="card mb-25 border-0 rounded-0 bg-white">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-2 text-muted">Chargement du résultat...</p>
      </div>

      <div v-else-if="notFound" class="text-center py-5">
        <i class="fa fa-info-circle text-warning" style="font-size:50px;"></i>
        <h5 class="mt-3">Aucun résultat disponible</h5>
        <p class="text-muted">Vous n'avez pas encore composé pour cette évaluation.</p>
        <router-link to="/liste-compositions" class="btn btn-outline-primary mt-2">
          Retour à la liste
        </router-link>
      </div>

      <div v-else-if="resultat && !resultat.estCorrige" class="text-center py-5">
        <i class="fa fa-hourglass-half text-warning" style="font-size:50px;"></i>
        <h5 class="mt-3">Correction en cours</h5>
        <p class="text-muted">{{ resultat.message || "Votre composition est en attente de correction par le professeur." }}</p>
        <p class="text-muted"><small>Code session : {{ resultat.sessionCode }}</small></p>
        <router-link to="/liste-compositions" class="btn btn-outline-primary mt-2">
          Retour à la liste
        </router-link>
      </div>

      <div v-else-if="resultat">

        <h3 class="mb-20">{{ resultat.composition?.titre }} — Résultat</h3>

        <div class="row g-3">
          <div class="col-6 col-md-3">
            <div class="result-card" :class="`result-${niveau}`">
              <h4 class="result-label">Score</h4>
              <p class="result-value" :class="`text-${niveau}`">{{ resultat.score }} / {{ resultat.total }}</p>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="result-card" :class="`result-${niveau}`">
              <h4 class="result-label">Pourcentage</h4>
              <p class="result-value" :class="`text-${niveau}`">{{ resultat.percentage }}%</p>
            </div>
          </div>
          <div class="col-6 col-md-3" v-if="resultat.composition?.type !== 'QuestionsReponses'">
            <div class="result-card result-success">
              <h4 class="result-label">Bonnes réponses</h4>
              <p class="result-value text-success">{{ resultat.correctAnswers }}</p>
            </div>
          </div>
          <div class="col-6 col-md-3" v-if="resultat.composition?.type !== 'QuestionsReponses'">
            <div class="result-card result-danger">
              <h4 class="result-label">Mauvaises réponses</h4>
              <p class="result-value text-danger">{{ resultat.incorrectAnswers }}</p>
            </div>
          </div>
        </div>

        <!-- ✅ MESSAGE D'ATTENTE SI PAS EXPIRÉ -->
        <div v-if="!resultat.dateExpire && !resultat.estProfesseur" class="alert alert-warning text-center mt-30">
          <i class="fa fa-clock-o fa-3x mb-3"></i>
          <h5 class="fw-bold">Réponses détaillées non disponibles</h5>
          <p class="mb-2">
            Les réponses détaillées seront visibles après la date de fin de soumission.
          </p>
          <p class="fw-bold text-dark mb-0">
            <i class="fa fa-calendar me-2"></i>
            Disponible après le : {{ resultat.dateFinSoumissionFormatee }}
          </p>
        </div>

        <!-- ✅ REVUE DÉTAILLÉE (si expiré OU professeur) -->
        <div v-else class="text-start mt-30">
          <h4 class="mb-20 text-center">Revue détaillée de vos réponses</h4>
          <div class="questions-review">

            <!-- Cas Questions/Réponses -->
            <template v-if="resultat.composition?.type === 'QuestionsReponses'">
              <div
                v-for="(question, index) in resultat.questionsWithAnswers"
                :key="question.id"
                class="question-review-item mb-15 border border-primary"
              >
                <div class="question-header px-15 py-10 bg-light">
                  <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0 fw-bold">Question {{ index + 1 }}</h5>
                    <span class="fw-bold text-primary">
                      {{ question.note ?? 0 }} / {{ question.bareme ?? '-' }}
                    </span>
                  </div>
                </div>

                <div class="question-content p-15 bg-white">
                  <p class="fw-semibold mb-10 text-black">{{ question.contenu }}</p>

                  <div class="user-answer mb-10 p-10 rounded bg-light">
                    <strong class="text-black">Votre réponse :</strong>
                    <p class="mb-0 mt-5" style="white-space: pre-wrap;">
                      {{ question.reponseTexte || "Non répondu" }}
                    </p>
                  </div>

                  <div v-if="question.commentaire" class="p-10 bg-info-subtle rounded">
                    <strong class="text-black">Commentaire du professeur :</strong>
                    <p class="mb-0 mt-5">{{ question.commentaire }}</p>
                  </div>
                </div>
              </div>
            </template>

            <!-- Cas QCM -->
            <template v-else>
              <div
                v-for="(question, index) in resultat.questionsWithAnswers"
                :key="question.id"
                class="question-review-item mb-15 border"
                :class="question.isCorrect ? 'border-success' : 'border-danger'"
              >
                <div class="question-header px-15 py-10" :class="question.isCorrect ? 'bg-success-subtle' : 'bg-danger-subtle'">
                  <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0 fw-bold">Question {{ index + 1 }}</h5>
                    <span class="status-icon" :class="question.isCorrect ? 'text-success' : 'text-danger'">
                      <i class="fa" :class="question.isCorrect ? 'fa-check-circle' : 'fa-times-circle'"></i>
                    </span>
                  </div>
                </div>

                <div class="question-content p-15 bg-white">
                  <p class="fw-semibold mb-10 text-black">{{ question.contenu }}</p>

                  <div class="user-answer mb-10 p-10 rounded bg-light">
                    <strong class="text-black">Votre réponse :</strong>
                    <span
                      class="ms-2 fw-semibold"
                      :class="question.isCorrect ? 'text-success' : 'text-danger text-decoration-line-through'"
                    >
                      {{ formatPropositions(question.userPropositions) }}
                    </span>
                  </div>

                  <div v-if="!question.isCorrect" class="correct-answer-display p-10 rounded">
                    <strong class="text-blue"><i class="fa fa-check me-1"></i>Bonne réponse :</strong>
                    <span class="ms-2 text-blue fw-bold">
                      {{ formatPropositions(question.correctPropositions) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>

          </div>
        </div>
        <!-- ✅ FIN REVUE DÉTAILLÉE -->

        <div class="actions-zone mt-30">
          <div v-if="peutRecommencer" class="retry-banner">
            <div class="retry-title">
              <i class="fa fa-info-circle me-2"></i>
              Vous pouvez recommencer cette composition !
            </div>
            <p class="retry-text">
              Vous avez encore <strong>{{ resultat.tentativesRestantes }}</strong> essai(s) restant(s).
            </p>
            <p class="retry-note">Seule la note du dernier essai sera retenue.</p>
          </div>

          <div class="actions-buttons">
            <button
              v-if="peutRecommencer"
              @click="recommencer"
              class="btn btn-warning btn-action"
            >
              <i class="fa fa-refresh me-2"></i>
              Recommencer ({{ resultat.tentativesRestantes }} restant{{ resultat.tentativesRestantes > 1 ? 's' : '' }})
            </button>

            <router-link to="/liste-compositions" class="btn btn-primary btn-action">
              <i class="fa fa-list me-2"></i> Retour à la liste
            </router-link>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import { error } from "@/utils/utils";

interface Proposition {
  id: number;
  contenu: string;
}

interface QuestionResultat {
  id: number;
  contenu: string;
  type: string;
  // QCM
  userPropositions?: Proposition[];
  correctPropositions?: Proposition[];
  isCorrect?: boolean | null;
  // QuestionsReponses
  reponseTexte?: string | null;
  note?: number | null;
  bareme?: number;
  commentaire?: string | null;
}

interface Resultat {
  sessionId?: number;
  sessionCode?: string;
  estCorrige: boolean;
  message?: string;
  score: number | null;
  total: number | null;
  percentage: number | null;
  correctAnswers: number | null;
  incorrectAnswers: number | null;
  questionsWithAnswers: QuestionResultat[];
  composition: { id: number; titre: string; type: string };
  dateExpire?: boolean;
  estProfesseur?: boolean;
  tentativesEffectuees?: number;
  nombreEssaisAutorise?: number;
  tentativesRestantes?: number;
  peutEncoreComposer?: boolean;
  dateFinSoumissionFormatee?: string;
}

export default defineComponent({
  name: "ResultatCompositionPage",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(true);
    const notFound = ref(false);
    const resultat = ref<Resultat | null>(null);
    const composition = ref<any>(null);

    const niveau = computed(() => {
      const p = resultat.value?.percentage ?? 0;
      if (p >= 70) return "success";
      if (p >= 50) return "warning";
      return "danger";
    });

    const peutRecommencer = computed(() => {
      if (!resultat.value) return false;
      
      const aEncoreDesEssais = resultat.value.peutEncoreComposer && (resultat.value.tentativesRestantes ?? 0) > 0;
      const dateNonExpiree = !resultat.value.dateExpire;
      
      return aEncoreDesEssais && dateNonExpiree;
    });

    onMounted(async () => {
      try {
        const { data } = await ApiService.get(`/compositions/${route.params.id}/resultat`);
        resultat.value = data.data;
      } catch (e: any) {
        if (e?.response?.status === 404) {
          notFound.value = true;
        } else {
          error(e?.response?.data?.message || "Erreur lors du chargement du résultat");
        }
      } finally {
        loading.value = false;
      }
    });

    function formatPropositions(props: Proposition[] | undefined): string {
      if (!props || props.length === 0) return "Non répondu";
      return props.map((p) => p.contenu).join(", ");
    }

    function recommencer() {
      router.push({
        name: 'DetailsCompositionPage',
        params: { id: route.params.id }
      });
    }

    return { loading, notFound, resultat, composition, niveau, peutRecommencer, formatPropositions, recommencer };
  },
});
</script>

<style scoped>
.result-card {
  height: 100%;
  padding: 16px 12px;
  text-align: center;
  background: #fff;
  border: none;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.result-success { background: #f3faf6; }
.result-warning { background: #fffaf0; }
.result-danger  { background: #fdf3f4; }

.result-label {
  margin: 0 0 6px;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}
.result-value {
  margin: 0;
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1.2;
}
.text-success { color: #157347 !important; }
.text-warning { color: #9a6700 !important; } /* ambre foncé : lisible sur fond clair */
.text-danger  { color: #c62828 !important; }

/* ---------- Revue détaillée (plus de scroll interne) ---------- */
.questions-review {
  padding-right: 0;
}
.question-review-item {
  border-width: 2px;
  border-radius: 6px;
  overflow: hidden;
}
.question-header {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
}
.status-icon {
  font-size: 1.6rem;
  line-height: 1;
}
.bg-success-subtle { background-color: #e6f4ea !important; }
.bg-danger-subtle { background-color: #f8d7da !important; }
.bg-info-subtle { background-color: #e7f1ff !important; }
.text-decoration-line-through {
  text-decoration: line-through;
  text-decoration-color: #dc3545;
  color: #dc3545 !important;
  font-weight: 600;
}
.user-answer {
  border-left: 4px solid #6c757d;
  background-color: #f8f9fa;
}
.correct-answer-display {
  border-left: 4px solid #0d6efd;
  background-color: #e7f1ff;
}
.text-blue { color: #0b5ed7; }

/* ---------- Bannière + boutons ---------- */
.actions-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.retry-banner {
  width: 100%;
  max-width: 640px;
  padding: 16px 20px;
  text-align: center;
  background: #e8f6fb;
  border: 1px solid #b6e3f0;
  border-radius: 8px;
  color: #064e5f;
}
.retry-title {
  font-weight: 700;
  font-size: 1.05rem;
}
.retry-text {
  margin: 6px 0 2px;
  color: #064e5f;
}
.retry-note {
  margin: 0;
  font-size: 0.875rem;
  color: #4b5563; /* plus foncé que text-muted */
}

.actions-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  width: 100%;
}
.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  min-width: 220px;
  padding: 0 28px;
  font-weight: 600;
}

/* Mobile : boutons pleine largeur, empilés */
@media (max-width: 575.98px) {
  .result-value { font-size: 1.7rem; }
  .btn-action {
    width: 100%;
    min-width: 0;
  }
}
</style>